import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { ModalShell, Field, TextInput, SelectInput, ModalFooter, ErrorBanner } from "../shared/FormElements";

function daysElapsedInMonth() {
  const now = new Date();
  return now.getDate();
}

export default function Team() {
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [teamRoles, setTeamRoles] = useState([]);
  const [hoursThisMonth, setHoursThisMonth] = useState({});
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddStaff, setShowAddStaff] = useState(false);
  const [assignModalFor, setAssignModalFor] = useState(null);
  const [hoursModalFor, setHoursModalFor] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);

    const monthStart = new Date();
    monthStart.setDate(1);
    const monthStartISO = monthStart.toISOString().slice(0, 10);

    const [staffRes, assignRes, rolesRes, hoursRes, clientsRes] = await Promise.all([
      supabase.from("users").select("*").in("role", ["agency_admin", "agency_staff"]),
      supabase.from("staff_client_assignments").select("*, clients(company_name)"),
      supabase.from("project_team_members").select("*, projects(name)").not("staff_user_id", "is", null),
      supabase.from("staff_hours").select("staff_user_id, hours_logged").gte("date", monthStartISO),
      supabase.from("clients").select("id, company_name").order("company_name"),
    ]);

    if (staffRes.error) setError(staffRes.error.message);
    setStaff(staffRes.data || []);
    setAssignments(assignRes.data || []);
    setTeamRoles(rolesRes.data || []);
    setClients(clientsRes.data || []);

    const hoursTotals = {};
    (hoursRes.data || []).forEach((row) => {
      hoursTotals[row.staff_user_id] = (hoursTotals[row.staff_user_id] || 0) + Number(row.hours_logged);
    });
    setHoursThisMonth(hoursTotals);

    setLoading(false);
  }

  async function removeAssignment(staffUserId, clientId) {
    await supabase
      .from("staff_client_assignments")
      .delete()
      .eq("staff_user_id", staffUserId)
      .eq("client_id", clientId);
    load();
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-xl font-semibold text-foreground">Team</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">{staff.length} team members</p>

      <ErrorBanner error={error} />

      <button
        onClick={() => setShowAddStaff(true)}
        className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold mb-5"
      >
        + Add staff member
      </button>

      <div className="flex flex-col gap-4">
        {staff.map((s) => {
          const staffAssignments = assignments.filter((a) => a.staff_user_id === s.id);
          const staffRoles = teamRoles.filter((r) => r.staff_user_id === s.id);
          const logged = hoursThisMonth[s.id] || 0;
          const days = daysElapsedInMonth();
          const possible = (s.weekly_capacity_hours || 40) * (days / 7);
          const utilization = possible > 0 ? Math.round((logged / possible) * 100) : 0;

          return (
            <div key={s.id} className="border border-border rounded-2xl bg-card p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                  {(s.full_name || s.email).slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">{s.full_name || s.email}</div>
                  <div className="font-mono text-[10.5px] uppercase text-muted-foreground">{s.role}</div>
                </div>
              </div>

              <div className="font-mono text-[10.5px] uppercase text-muted-foreground mt-3 mb-1.5">
                Assigned clients
              </div>
              <div className="flex flex-wrap gap-2">
                {staffAssignments.map((a) => (
                  <span key={a.client_id} className="text-xs bg-accent text-foreground rounded-full px-2.5 py-1 flex items-center gap-1.5">
                    {a.clients?.company_name}
                    <button onClick={() => removeAssignment(s.id, a.client_id)} className="text-muted-foreground">
                      x
                    </button>
                  </span>
                ))}
                <button
                  onClick={() => setAssignModalFor(s)}
                  className="text-xs border border-dashed border-input rounded-full px-2.5 py-1 text-muted-foreground"
                >
                  + Assign client
                </button>
              </div>

              {staffRoles.length > 0 && (
                <>
                  <div className="font-mono text-[10.5px] uppercase text-muted-foreground mt-3 mb-1.5">
                    Project roles
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {staffRoles.map((r) => (
                      <span key={r.id} className="text-xs bg-accent text-foreground rounded-full px-2.5 py-1">
                        {r.projects?.name} <span className="font-mono text-[10px] text-muted-foreground">{r.title}</span>
                      </span>
                    ))}
                  </div>
                </>
              )}

              <div className="flex items-center gap-5 mt-4 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  Capacity: <strong className="text-foreground">{s.weekly_capacity_hours || 40} hrs/week</strong>
                </span>
                <span className="text-xs text-muted-foreground">
                  Logged this month: <strong className="text-foreground">{logged} hrs</strong>
                </span>
                <span className="text-xs text-muted-foreground">
                  Utilization: <strong className="text-foreground">{utilization}%</strong>
                </span>
                <button
                  onClick={() => setHoursModalFor(s)}
                  className="ml-auto text-xs border border-input rounded-full px-3 py-1.5"
                >
                  + Log hours
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showAddStaff && (
        <AddStaffModal
          onClose={() => setShowAddStaff(false)}
          onSaved={() => {
            setShowAddStaff(false);
            load();
          }}
        />
      )}

      {assignModalFor && (
        <AssignClientModal
          staffMember={assignModalFor}
          clients={clients}
          onClose={() => setAssignModalFor(null)}
          onSaved={() => {
            setAssignModalFor(null);
            load();
          }}
        />
      )}

      {hoursModalFor && (
        <LogHoursModal
          staffMember={hoursModalFor}
          clients={clients}
          onClose={() => setHoursModalFor(null)}
          onSaved={() => {
            setHoursModalFor(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function AddStaffModal({ onClose, onSaved }) {
  const [userId, setUserId] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agency_staff");
  const [capacity, setCapacity] = useState(40);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!userId.trim() || !fullName.trim() || !email.trim()) {
      setError("User ID, name, and email are required.");
      return;
    }
    setSaving(true);
    const { error: insertError } = await supabase.from("users").insert({
      id: userId.trim(),
      full_name: fullName.trim(),
      email: email.trim(),
      role,
      weekly_capacity_hours: Number(capacity) || 40,
    });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell
      title="Add staff member"
      subtitle="Create their login in Supabase first (Authentication, Users, Add user), then paste their user ID here"
      onClose={onClose}
    >
      <ErrorBanner error={error} />
      <Field label="Supabase user ID" hint="Copied from Authentication > Users after creating their login there">
        <TextInput value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="e.g. 3f1c2a9b-..." />
      </Field>
      <Field label="Full name">
        <TextInput value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Rohan Kulkarni" />
      </Field>
      <Field label="Email">
        <TextInput value={email} onChange={(e) => setEmail(e.target.value)} placeholder="rohan@abhinavjoshi.com" />
      </Field>
      <Field label="Role">
        <SelectInput value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="agency_staff">Agency staff</option>
          <option value="agency_admin">Agency admin</option>
        </SelectInput>
      </Field>
      <Field label="Weekly capacity (hours)">
        <TextInput type="number" value={capacity} onChange={(e) => setCapacity(e.target.value)} />
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}

function AssignClientModal({ staffMember, clients, onClose, onSaved }) {
  const [clientId, setClientId] = useState(clients[0]?.id || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!clientId) return;
    setSaving(true);
    const { error: insertError } = await supabase
      .from("staff_client_assignments")
      .insert({ staff_user_id: staffMember.id, client_id: clientId });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell title={`Assign a client to ${staffMember.full_name || staffMember.email}`} onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Client">
        <SelectInput value={clientId} onChange={(e) => setClientId(e.target.value)}>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}

function LogHoursModal({ staffMember, clients, onClose, onSaved }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [hours, setHours] = useState("");
  const [clientId, setClientId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    if (!date || !hours) {
      setError("Date and hours are required.");
      return;
    }
    setSaving(true);
    const { error: insertError } = await supabase.from("staff_hours").insert({
      staff_user_id: staffMember.id,
      date,
      hours_logged: parseFloat(hours),
      client_id: clientId || null,
    });
    setSaving(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    onSaved();
  }

  return (
    <ModalShell title={`Log hours for ${staffMember.full_name || staffMember.email}`} onClose={onClose}>
      <ErrorBanner error={error} />
      <Field label="Date">
        <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label="Hours">
        <TextInput type="number" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="8" />
      </Field>
      <Field label="Client (optional)">
        <SelectInput value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Not client specific</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.company_name}
            </option>
          ))}
        </SelectInput>
      </Field>
      <ModalFooter onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalShell>
  );
}
